import { VerdictBadge, AiPct } from "../components/items/LiveCheckPanel";
import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Layout from "../components/Layout";
import { StatusBadge, TypeBadge, TalmiaBadge } from "../components/Badges";
import { Plus, ArrowUp, ArrowDown, ArrowUpDown, ChevronLeft, ChevronRight, Trash2 } from "lucide-react";
import { STATUS_ORDER, PRIORITY_ORDER, TYPE_META, STATUSES, PRIORITIES, TYPES, EFFORTS, IMPACTS, PHASES } from "../constants/enums";
import { assigneeLabel, assigneeShort } from "../lib/assignees";
import AssigneeSelect from "../components/dev-tracker/AssigneeSelect";
import InfiniteSentinel from "../components/InfiniteSentinel";
import { useProgressiveList } from "../hooks/useProgressiveList";

// Column registry for the Dev Tracker table (Wave 2). `sort` marks a
// sortable header key; `editable` enables inline edit when the table is in
// editable mode.
export const ITEM_COLUMNS = {
  title:          { label: "Title",    sort: "title" },
  talmia_version: { label: "Sys",      sort: "talmia_version", editable: true },
  area:           { label: "Area",     editable: true },
  type:           { label: "Type",     sort: "type", editable: true },
  status:         { label: "Status",   sort: "status", editable: true },
  priority:       { label: "Priority", sort: "priority", editable: true },
  function:       { label: "Function", editable: true },
  effort:         { label: "Effort",   sort: "effort", editable: true },
  impact:         { label: "Impact",   sort: "impact", editable: true },
  roi:            { label: "ROI",      sort: "roi", editable: true },
  phase:          { label: "Phase",    editable: true },
  assigned_to:    { label: "Assignee", sort: "assigned_to", editable: true },
  ai_pct:         { label: "AI %",     sort: "ai_pct" },
  updated_at:     { label: "Updated",  sort: "updated_at" },
};
// Original 8-column layout — used by the Dashboard "My Items" table.
export const DEFAULT_COLUMNS = ["title", "talmia_version", "area", "type", "status", "priority", "assigned_to", "updated_at"];
// Wave 2 default for the full tracker (Effort/Impact/Function off by default).
export const TRACKER_DEFAULT_COLUMNS = ["title", "talmia_version", "area", "type", "status", "priority", "roi", "phase", "assigned_to", "ai_pct", "updated_at"];

export default function Dashboard() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [it, us] = await Promise.all([
          api.get("/items"),
          api.get("/users"),
        ]);
        setItems(it.data);
        setUsers(us.data);
      } finally { setLoading(false); }
    })();
  }, []);

  const usersById = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);

  const myItems = items.filter((i) => (i.assigned_to === user?.id || i.created_by === user?.id) && i.status !== "Done" && i.status !== "Closed");

  return (
    <Layout>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight" data-testid="dashboard-title">
            My Items
          </h1>
          <p className="text-slate-400 mt-1">Items you own or created that are still open.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link to="/items/new" className="btn-coral inline-flex items-center gap-2" data-testid="new-item-cta">
            <Plus className="w-4 h-4" /> New Item
          </Link>
        </div>
      </div>

      <div className="card-dark p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl font-bold">My Work</h2>
          <Link to="/items/all" className="text-sm text-orange-400 hover:text-orange-300" data-testid="view-all-link">View all →</Link>
        </div>
        <ItemTable items={myItems} usersById={usersById} loading={loading} emptyMessage="Nothing assigned or created by you yet." />
      </div>
    </Layout>
  );
}

function SortHeaderCell({ label, k, sort, onToggle }) {
  const active = sort.key === k;
  const Icon = active ? (sort.dir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <th>
      <button
        type="button"
        onClick={() => onToggle(k)}
        className={`inline-flex items-center gap-1 uppercase tracking-wider text-xs font-semibold hover:opacity-80 transition-opacity ${active ? "text-white" : "text-white/80"}`}
        data-testid={`sort-header-${k}`}
      >
        {label}
        <Icon className={`w-3 h-3 ${active ? "opacity-100" : "opacity-50"}`} />
      </button>
    </th>
  );
}

// ─── Prettier row primitives ─────────────────────────────────────────
// Priority displayed as a coloured dot + label instead of a wide pill;
// keeps the row visually calm at 8-column widths.
const PRIORITY_DOT = {
  Critical: "bg-red-400 ring-red-400/30",
  Urgent:   "bg-red-400 ring-red-400/30",
  High:     "bg-orange-400 ring-orange-400/30",
  Medium:   "bg-amber-400 ring-amber-400/30",
  Low:      "bg-sky-400 ring-sky-400/30",
};

function PriorityDot({ value }) {
  const tone = PRIORITY_DOT[value] || PRIORITY_DOT.Medium;
  return (
    <span className="inline-flex items-center gap-2 text-[11.5px] text-slate-200" data-testid={`priority-dot-${value}`}>
      <span className={`w-2 h-2 rounded-full ring-4 ${tone}`} />
      {value}
    </span>
  );
}

// Circle initial badge for the assignee column — much cleaner than
// spelling out full names on every row.
function Avatar({ name, size = "sm" }) {
  if (!name) return <span className="text-slate-500 text-xs">Unassigned</span>;
  const initials = name.split(/\s+/).map((n) => n[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "?";
  // Colour based on hash of name so each person is consistent.
  const palette = ["bg-orange-500/20 text-orange-200 border-orange-400/30", "bg-teal-500/20 text-teal-200 border-teal-400/30",
                   "bg-purple-500/20 text-purple-200 border-purple-400/30", "bg-emerald-500/20 text-emerald-200 border-emerald-400/30",
                   "bg-sky-500/20 text-sky-200 border-sky-400/30", "bg-fuchsia-500/20 text-fuchsia-200 border-fuchsia-400/30"];
  let h = 0; for (let i = 0; i < name.length; i++) h = (h << 5) - h + name.charCodeAt(i);
  const tone = palette[Math.abs(h) % palette.length];
  const dim = size === "xs" ? "w-5 h-5 text-[9px]" : "w-6 h-6 text-[10px]";
  return (
    <span className="inline-flex items-center gap-2">
      <span className={`grid place-items-center rounded-full border font-mono font-bold ${dim} ${tone}`} title={name}>{initials}</span>
      <span className="text-[11.5px] text-slate-200 truncate max-w-[110px]">{name}</span>
    </span>
  );
}

// Relative-time helper: "2h ago", "3d ago", falls back to date for older.
function timeAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60)   return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

// Icon + label for the Type column — same info as before, less pill,
// more scannable.
function TypeCell({ value }) {
  const meta = TYPE_META[value] || {};
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11.5px] text-slate-200`} data-testid={`type-cell-${value}`}>
      <span className="text-base leading-none">{meta.icon || "•"}</span>
      {value}
    </span>
  );
}

export function ItemTable({ items, usersById, loading, emptyMessage = "No items", onDelete = null, isAdmin = false, selectedIds = null, onSelectionChange = null, areasById = {}, infinite = false, onCounts = null, editable = false, onPatchItem = null, visibleColumns = null, users = [], areas = [] }) {
  const [sort, setSort] = useState({ key: "smart", dir: "asc" });
  const [editing, setEditing] = useState(null); // { id, field }
  const canEdit = editable && !!onPatchItem;
  const cols = visibleColumns || DEFAULT_COLUMNS;
  const commitPatch = (id, patch) => {
    setEditing(null);
    Promise.resolve(onPatchItem(id, patch)).then(() => toast.success("Updated")).catch(() => toast.error("Update failed"));
  };
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const { visibleCount, showMore } = useProgressiveList(`${items.length}|${sort.key}|${sort.dir}`, 50);
  const selectionEnabled = !!onSelectionChange;
  const selSet = useMemo(() => new Set(selectedIds || []), [selectedIds]);

  const sorted = useMemo(() => {
    const getVal = (it, key) => {
      if (key === "smart") {
        const s = STATUS_ORDER[it.status] ?? 99;
        const p = PRIORITY_ORDER[it.priority] ?? 99;
        return s * 10 + p;
      }
      if (key === "priority") return PRIORITY_ORDER[it.priority] ?? 99;
      if (key === "status") return STATUS_ORDER[it.status] ?? 99;
      if (key === "assigned_to") return (assigneeLabel(it.assigned_to, usersById) || "~").toLowerCase();
      if (key === "created_by") return (usersById[it.created_by]?.name || "~").toLowerCase();
      if (key === "title") return (it.title || "").toLowerCase();
      if (key === "type") return it.type || "";
      if (key === "roi" || key === "impact") return Number(it[key] ?? -Infinity);
      if (key === "ai_pct") return it.ai_verdict?.verdict ? (it.ai_verdict.completion ?? (it.ai_verdict.verdict === "done" ? 100 : 0)) : -1;
      if (key === "effort") return ({ S: 0, M: 1, L: 2 })[it.effort] ?? -1;
      if (key === "updated_at" || key === "created_at") return new Date(it[key] || 0).getTime();
      return it[key];
    };
    const arr = [...items];
    arr.sort((a, b) => {
      const va = getVal(a, sort.key), vb = getVal(b, sort.key);
      if (va < vb) return sort.dir === "asc" ? -1 : 1;
      if (va > vb) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [items, sort, usersById]);

  const toggle = (key) => {
    setPage(1);
    setSort((s) => s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });
  };

  useEffect(() => { setPage(1); }, [items.length, pageSize]);

  const total = sorted.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);
  const startIdx = infinite ? 0 : (safePage - 1) * pageSize;
  const pageItems = infinite ? sorted.slice(0, visibleCount) : sorted.slice(startIdx, startIdx + pageSize);

  useEffect(() => { if (infinite && onCounts) onCounts(pageItems.length, total); }, [infinite, onCounts, pageItems.length, total]);

  const dash = <span className="text-slate-600 text-[11px]">—</span>;
  const areasForSystem = (sys) => (sys === "1" || sys === "2") ? areas.filter((a) => a.system === sys) : areas;

  const renderDisplay = (key, it) => {
    switch (key) {
      case "title": {
        const typeIcon = TYPE_META[it.type]?.icon;
        return (
          <Link to={`/items/${it.id}`} className="flex items-center gap-2 hover:text-white group">
            <span className="text-base leading-none opacity-70 group-hover:opacity-100">{typeIcon || "•"}</span>
            <span className="line-clamp-1 text-slate-100 group-hover:text-orange-300">{it.title}</span>
            {it.ai_verdict && <VerdictBadge v={it.ai_verdict} />}{it.archived && <span className="text-[10px] font-mono text-slate-500 border border-white/10 rounded px-1.5">archived</span>}
          </Link>
        );
      }
      case "talmia_version": return <TalmiaBadge value={it.talmia_version} />;
      case "area": {
        const area = it.menu_area_id ? areasById[it.menu_area_id] : null;
        const areaLabel = area?.label || it.menu_area;
        const areaColour = area?.colour || "#94a3b8";
        return areaLabel ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-medium border"
            style={{ borderColor: areaColour + "66", background: areaColour + "22", color: areaColour }}
            data-testid={`item-area-${it.id}`}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: areaColour }} />{areaLabel}
          </span>
        ) : dash;
      }
      case "type": return <TypeCell value={it.type} />;
      case "status": return <StatusBadge value={it.status} />;
      case "priority": return <PriorityDot value={it.priority} />;
      case "function": return it.function ? <span className="text-slate-200 text-[12px] line-clamp-1 max-w-[160px]" title={it.function}>{it.function}</span> : dash;
      case "effort": return it.effort ? <span className="text-[11px] font-mono px-1.5 py-0.5 rounded border border-white/15 text-slate-200">{it.effort}</span> : dash;
      case "impact": return (it.impact != null) ? <span className="text-[12px] text-slate-200 font-mono">{it.impact}</span> : dash;
      case "roi": return (it.roi != null && it.roi !== "") ? <span className="text-[12px] font-mono text-emerald-300">{it.roi}</span> : dash;
      case "phase": return it.phase ? <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border border-white/10 text-slate-300">{it.phase}</span> : dash;
      case "assigned_to": return <Avatar name={assigneeLabel(it.assigned_to, usersById)} />;
      case "ai_pct": return <AiPct v={it.ai_verdict} testid={`ai-pct-${it.id}`} />;
      case "updated_at": return <span className="text-slate-500 text-[11.5px] font-mono" title={new Date(it.updated_at).toLocaleString()}>{timeAgo(it.updated_at)}</span>;
      default: return dash;
    }
  };

  const done = () => setEditing(null);
  const enumSelect = (it, field, opts, extra) => (
    <select autoFocus defaultValue={it[field] ?? ""} onBlur={done} data-testid={`edit-${field}-${it.id}`}
      onChange={(e) => commitPatch(it.id, extra ? extra(e.target.value) : { [field]: e.target.value === "" ? null : e.target.value })}
      className="input-dark py-1 pl-1.5 pr-6 text-xs w-auto">
      {opts.map((o) => <option key={String(o.value)} value={o.value}>{o.label}</option>)}
    </select>
  );

  const renderEditor = (key, it) => {
    switch (key) {
      case "status":   return enumSelect(it, "status", STATUSES.map((v) => ({ value: v, label: v })));
      case "priority": return enumSelect(it, "priority", PRIORITIES.map((v) => ({ value: v, label: v })));
      case "type":     return enumSelect(it, "type", TYPES.map((v) => ({ value: v, label: v })));
      case "phase":    return enumSelect(it, "phase", [{ value: "", label: "—" }, ...PHASES.map((v) => ({ value: v, label: v }))]);
      case "effort":   return enumSelect(it, "effort", [{ value: "", label: "—" }, ...EFFORTS.map((v) => ({ value: v, label: v }))]);
      case "impact":   return enumSelect(it, "impact", [{ value: "", label: "—" }, ...IMPACTS.map((v) => ({ value: v, label: v }))], (v) => ({ impact: v === "" ? null : Number(v) }));
      case "talmia_version": return enumSelect(it, "talmia_version",
        [{ value: "1", label: "Talmia 1" }, { value: "2", label: "Talmia 2" }, { value: "both", label: "Both" }],
        (v) => ({ talmia_version: v, menu_area: null, menu_area_id: null }));
      case "area": return enumSelect(it, "menu_area_id",
        [{ value: "", label: "— none —" }, ...areasForSystem(it.talmia_version).map((a) => ({ value: a.id, label: a.label }))],
        (v) => { const a = areas.find((x) => x.id === v); return a ? { menu_area: a.label, menu_area_id: a.id } : { menu_area: null, menu_area_id: null }; });
      case "assigned_to": return <AssigneeSelect autoFocus value={it.assigned_to} users={users} onBlur={done} className="py-1 pr-6 text-xs w-auto" testid={`edit-assignee-${it.id}`} onChange={(v) => commitPatch(it.id, { assigned_to: v })} />;
      case "roi": return <InlineNumber value={it.roi} onCommit={(v) => commitPatch(it.id, { roi: v })} onCancel={done} testid={`edit-roi-${it.id}`} />;
      default: return renderDisplay(key, it);
    }
  };

  const Cell = (key, it) => {
    const col = ITEM_COLUMNS[key];
    const isEditingCell = canEdit && col.editable && editing && editing.id === it.id && editing.field === key;
    return (
      <td key={key} className="px-4 py-3">
        {isEditingCell ? renderEditor(key, it)
          : (canEdit && col.editable
              ? <button type="button" onClick={() => setEditing({ id: it.id, field: key })} className="text-left hover:bg-white/5 rounded px-1 -mx-1 py-0.5 min-w-[24px] inline-block" data-testid={`cell-${key}-${it.id}`}>{renderDisplay(key, it)}</button>
              : renderDisplay(key, it))}
      </td>
    );
  };

  if (loading) return <div className="p-6 text-slate-400 text-sm">Loading...</div>;
  if (!items.length) return <div className="p-6 text-slate-400 text-sm" data-testid="empty-state">{emptyMessage}</div>;
  return (
    <div>
      <div className="overflow-x-auto rounded-xl border border-white/5">
      <table className="w-full text-sm" style={{ minWidth: cols.length > 8 ? `${cols.length * 120}px` : undefined }} data-testid="items-table">
        <thead className="tbl-header">
          <tr>
            {selectionEnabled && (
              <th className="w-8 px-3 py-3">
                <input
                  type="checkbox"
                  checked={pageItems.length > 0 && pageItems.every((it) => selSet.has(it.id))}
                  onChange={(e) => {
                    if (!onSelectionChange) return;
                    if (e.target.checked) {
                      const next = new Set(selSet);
                      pageItems.forEach((it) => next.add(it.id));
                      onSelectionChange([...next]);
                    } else {
                      const pageIds = new Set(pageItems.map((it) => it.id));
                      onSelectionChange((selectedIds || []).filter((id) => !pageIds.has(id)));
                    }
                  }}
                  aria-label="Select all rows on this page"
                  data-testid="items-select-all"
                />
              </th>
            )}
            {cols.map((key) => {
              const col = ITEM_COLUMNS[key];
              return col.sort
                ? <SortHeaderCell key={key} label={col.label} k={col.sort} sort={sort} onToggle={toggle} />
                : <th key={key} className="text-xs uppercase tracking-wider font-semibold text-white/80 px-4 py-3 text-left">{col.label}</th>;
            })}
            {isAdmin && onDelete && <th className="text-xs uppercase tracking-wider font-semibold text-white/80 px-4 py-3 text-right">·</th>}
          </tr>
        </thead>
        <tbody className="tbl-body">
          {pageItems.map((it, i) => {
            const rowShade = i % 2 === 0 ? "" : "bg-white/[0.015]";
            const rowSelected = selSet.has(it.id);
            return (
              <tr
                key={it.id}
                data-testid={`item-row-${it.id}`}
                className={`${rowShade} ${rowSelected ? "bg-orange-500/[0.06]" : ""} hover:bg-white/[0.04] transition-colors`}
              >
                {selectionEnabled && (
                  <td className="px-3">
                    <input
                      type="checkbox"
                      checked={rowSelected}
                      onChange={(e) => {
                        e.stopPropagation();
                        if (!onSelectionChange) return;
                        onSelectionChange(
                          rowSelected
                            ? (selectedIds || []).filter((x) => x !== it.id)
                            : [...(selectedIds || []), it.id],
                        );
                      }}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Select ${it.title}`}
                      data-testid={`item-select-${it.id}`}
                    />
                  </td>
                )}
                {cols.map((key) => Cell(key, it))}
                {isAdmin && onDelete && (
                  <td className="text-right">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onDelete(it); }}
                      className="opacity-0 group-hover:opacity-100 hover:opacity-100 inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10.5px] font-medium text-rose-300 hover:text-rose-200 hover:bg-rose-500/[0.12] border border-transparent hover:border-rose-500/30"
                      data-testid={`delete-item-${it.id}`}
                      aria-label="Delete item"
                    >
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mt-3 px-1">
        <div className="text-xs text-slate-400" data-testid="pagination-summary">
          {infinite ? (
            <>Showing <span className="text-slate-200 font-medium">{pageItems.length}</span> of <span className="text-slate-200 font-medium">{total}</span></>
          ) : (
            <>Showing <span className="text-slate-200 font-medium">{total === 0 ? 0 : startIdx + 1}</span>–<span className="text-slate-200 font-medium">{startIdx + pageItems.length}</span> of <span className="text-slate-200 font-medium">{total}</span></>
          )}
        </div>
        {!infinite && (
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <span>Rows</span>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="input-dark py-1 pl-2 pr-7 text-xs w-auto"
              data-testid="pagination-page-size"
            >
              {[5, 10, 25, 50].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage <= 1}
              className="p-1.5 rounded-md border border-white/10 text-slate-300 hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="pagination-prev"
              aria-label="Previous page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-xs text-slate-300 px-2" data-testid="pagination-current">
              Page <span className="text-white font-medium">{safePage}</span> / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage >= totalPages}
              className="p-1.5 rounded-md border border-white/10 text-slate-300 hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="pagination-next"
              aria-label="Next page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
        )}
      </div>
      {infinite && total > 0 && (
        <InfiniteSentinel
          onReach={showMore}
          hasMore={pageItems.length < total}
          loading={false}
          count={pageItems.length}
          total={total}
          testId="items-sentinel"
        />
      )}
    </div>
  );
}


// Inline numeric editor (ROI). Commits on Enter/blur, cancels on Escape.
function InlineNumber({ value, onCommit, onCancel, testid }) {
  const [v, setV] = useState(value ?? "");
  const save = () => {
    const trimmed = String(v).trim();
    onCommit(trimmed === "" ? null : Number(trimmed));
  };
  return (
    <input
      autoFocus
      type="number"
      step="0.1"
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={save}
      onKeyDown={(e) => {
        if (e.key === "Enter") save();
        else if (e.key === "Escape") onCancel();
      }}
      className="input-dark py-1 px-1.5 text-xs w-16"
      data-testid={testid}
    />
  );
}
